"use client";

import { useEffect } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

/**
 * Motion engine for the /sobre page. Same language as HomeMotion but without
 * the rocket/orbit-dot/pillar-orb timeline — uses the hero text stagger, the
 * mission console fade-in, parallax of nebulas, reveals, counters, magnetic
 * CTAs, the principle-card 3D tilt and the scroll progress bar.
 *
 * Every entrance is `gsap.from(...)`, so content stays visible if JS fails.
 * prefers-reduced-motion users get the page with no animation.
 */
export function SobreMotion() {
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      document.querySelectorAll<HTMLElement>(".counter").forEach((el) => {
        const to = Number(el.dataset.to || "0");
        el.textContent = (el.dataset.prefix || "") + to.toLocaleString("pt-BR") + (el.dataset.suffix || "");
      });
      // Make sure the console (which starts at opacity:0 in CSS) is visible.
      document.querySelectorAll<HTMLElement>(".console--sobre").forEach((el) => {
        el.style.opacity = "1";
      });
      return;
    }
    gsap.registerPlugin(ScrollTrigger);

    const ctx = gsap.context(() => {
      // Hero intro
      const heroTl = gsap.timeline({ defaults: { ease: "power3.out" }, delay: 0.2 });
      heroTl
        .from(".sobre-hero .crumb", { opacity: 0, y: 8, duration: 0.5 })
        .from(".sobre-hero .hero__telemetry", { opacity: 0, y: 10, duration: 0.7 }, "-=0.2")
        .from(".sobre-hero .hero__title .char", { yPercent: 110, opacity: 0, duration: 0.7, stagger: 0.012, ease: "power3.out" }, "-=0.4")
        .from(".sobre-hero .hero__sub", { opacity: 0, y: 10, duration: 0.6 }, "-=0.3")
        .from(".sobre-hero .hero__ctas", { opacity: 0, y: 10, duration: 0.6 }, "-=0.4")
        .from(".sobre-hero .hero__micro", { opacity: 0, duration: 0.5 }, "-=0.3");

      // Mission console (timeline) fade-in
      gsap.from(".console--sobre", { opacity: 0, x: 30, duration: 1, delay: 1.0, ease: "power3.out" });
      gsap.from(".console--sobre .console__row", { opacity: 0, x: 30, duration: 0.5, stagger: 0.12, ease: "power2.out", delay: 1.2 });

      // Soft rotating orbits on /sobre (no rocket)
      gsap.to(".sobre-hero .orbit--1", { rotation: 360, duration: 40, repeat: -1, ease: "none", transformOrigin: "50% 50%" });
      gsap.to(".sobre-hero .orbit--2", { rotation: -360, duration: 64, repeat: -1, ease: "none", transformOrigin: "50% 50%" });

      // Stage parallax tied to the hero
      gsap.to(".sobre-hero__stage", {
        y: -80,
        scrollTrigger: { trigger: ".sobre-hero", start: "top top", end: "bottom top", scrub: 1 },
      });
      gsap.to(".cosmos__nebula--a", { y: 200, scrollTrigger: { trigger: "body", start: "top top", end: "bottom top", scrub: 1 } });
      gsap.to(".cosmos__nebula--b", { y: -260, scrollTrigger: { trigger: "body", start: "top top", end: "bottom top", scrub: 1 } });
      gsap.to(".cosmos__nebula--c", { y: 180, scrollTrigger: { trigger: "body", start: "top top", end: "bottom top", scrub: 1 } });

      // Reveals (.reveal / .reveal-tilt / .reveal-fade)
      gsap.utils.toArray<HTMLElement>(".reveal").forEach((el) => {
        gsap.from(el, { opacity: 0, y: 40, duration: 0.9, ease: "power3.out", scrollTrigger: { trigger: el, start: "top 88%", toggleActions: "play none none none" } });
      });
      gsap.utils.toArray<HTMLElement>(".reveal-tilt").forEach((el) => {
        gsap.from(el, { opacity: 0, y: 60, rotateX: -12, duration: 1, ease: "power3.out", transformOrigin: "center top", scrollTrigger: { trigger: el, start: "top 88%", toggleActions: "play none none none" } });
      });
      gsap.utils.toArray<HTMLElement>(".reveal-fade").forEach((el) => {
        gsap.from(el, { opacity: 0, duration: 1.2, ease: "power2.out", scrollTrigger: { trigger: el, start: "top 92%", toggleActions: "play none none none" } });
      });

      // Stat counters
      gsap.utils.toArray<HTMLElement>(".counter").forEach((el) => {
        const end = parseFloat(el.dataset.to || "0");
        const prefix = el.dataset.prefix || "";
        const suffix = el.dataset.suffix || "";
        const obj = { v: 0 };
        ScrollTrigger.create({
          trigger: el,
          start: "top 85%",
          once: true,
          onEnter: () => {
            el.textContent = prefix + "0" + suffix;
            gsap.to(obj, {
              v: end,
              duration: 1.8,
              ease: "power3.out",
              onUpdate: () => {
                el.textContent = prefix + Math.round(obj.v).toLocaleString("pt-BR") + suffix;
              },
            });
          },
        });
      });

      // Principle cards — 3D tilt + spotlight (same as .pillar on the home)
      document.querySelectorAll<HTMLElement>(".principle").forEach((card) => {
        const onMove = (e: MouseEvent) => {
          const r = card.getBoundingClientRect();
          const x = (e.clientX - r.left) / r.width;
          const y = (e.clientY - r.top) / r.height;
          card.style.setProperty("--mx", x * 100 + "%");
          card.style.setProperty("--my", y * 100 + "%");
          gsap.to(card, { rotateX: (0.5 - y) * 8, rotateY: (x - 0.5) * 10, duration: 0.4, ease: "power2.out" });
        };
        const onLeave = () => gsap.to(card, { rotateX: 0, rotateY: 0, duration: 0.6, ease: "power3.out" });
        card.addEventListener("mousemove", onMove);
        card.addEventListener("mouseleave", onLeave);
      });

      // Magnetic CTAs
      document.querySelectorAll<HTMLElement>(".btn-magnetic").forEach((btn) => {
        const onMove = (e: MouseEvent) => {
          const r = btn.getBoundingClientRect();
          const x = e.clientX - r.left - r.width / 2;
          const y = e.clientY - r.top - r.height / 2;
          gsap.to(btn, { x: x * 0.18, y: y * 0.3, duration: 0.4, ease: "power2.out" });
        };
        const onLeave = () => gsap.to(btn, { x: 0, y: 0, duration: 0.5, ease: "elastic.out(1, 0.4)" });
        btn.addEventListener("mousemove", onMove);
        btn.addEventListener("mouseleave", onLeave);
      });

      // Scroll progress
      const progress = document.querySelector<HTMLElement>(".scroll-progress");
      if (progress) {
        ScrollTrigger.create({
          start: 0,
          end: "max",
          onUpdate: (self) => {
            progress.style.width = self.progress * 100 + "%";
          },
        });
      }

      // Hue shift when entering "Liderança"
      gsap.to(".cosmos", {
        filter: "hue-rotate(30deg)",
        scrollTrigger: { trigger: "#lideranca", start: "top center", end: "bottom center", scrub: true },
      });

      requestAnimationFrame(() => ScrollTrigger.refresh());
    });

    return () => ctx.revert();
  }, []);

  return null;
}
